import moment from "moment"
import { CDN_URL } from "~constants/endpoints"
import FileUploadService from "~services/FileUploadService"
import { imagePathName } from "~utils/fileNameGenerator"
import { isArray } from "lodash"

export const sanitizeJson = (json: string) => {
  return json.replace(/[\u0000]+/g, "")
}

export const decodeJWT = (token: string) => {
  if (token.length === 0) {
    return null
  }

  const headerPart = sanitizeJson(atob(token.split(".")[0]))
  const payloadPart = sanitizeJson(atob(token.split(".")[1]))

  return {
    header: JSON.parse(headerPart),
    payload: JSON.parse(payloadPart),
  }
}

export const urlQueryBuilder = (options: object): string => {
  const query = Object.keys(options)
    .map(key => `${key}=${options[key]}`)
    .join("&")

  return query
}

export const getFullImagePath = (originalPath: string) => {
  return originalPath?.startsWith("http")
    ? originalPath
    : CDN_URL + originalPath
}

export const isEmpty = (obj: object) => {
  if (!obj) return true
  return Object.keys(obj).length == 0
}

export enum EntityStatus {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

export const toggleStatus = (status: string) =>
  status === EntityStatus.ACTIVE ? EntityStatus.INACTIVE : EntityStatus.ACTIVE

export const todayISO = () => moment().format("YYYY-MM-DD")

export const getTodayRangeISO = () => {
  const today = todayISO()
  return today + "," + today
}

export const getLast7daysRangeISO = () => {
  const startDate = moment().subtract(7, "days").format("YYYY-MM-DD")
  return [startDate, todayISO()]
}

export const getLast14daysRangeISO = () => {
  const startDate = moment().subtract(14, "days").format("YYYY-MM-DD")
  return [startDate, todayISO()]
}

export const getLast28daysRangeISO = () => {
  const startDate = moment().subtract(28, "days").format("YYYY-MM-DD")
  return [startDate, todayISO()]
}

export enum DateRange {
  LAST_7_DAYS = "7",
  LAST_14_DAYS = "14",
  LAST_28_DAYS = "28",
}

export const formatDateTimeISO = (date: string) =>
  moment(new Date(date)).format("MM/DD/YYYY")

export const sanitizeObjectAttributes = (original: any): any => {
  for (const propName in original) {
    if (original[propName] === "" || original[propName] === undefined) {
      original[propName] = null
    }
  }
  return original
}

export const trimObject = (obj: {
  [key: string]: any
}): { [key: string]: any } => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (typeof value === "object" && value !== null && "val" in value) {
        return [key, value.val]
      }
      return [key, typeof value === "string" ? value.trim() : value]
    }),
  )
}

export const getObjectValues = (
  obj: { [key: string]: any } | unknown,
): { [key: string]: undefined } => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (
        typeof value === "object" &&
        value !== null &&
        value.hasOwnProperty("label") &&
        value.hasOwnProperty("value")
      ) {
        return [key, value.value]
      }
      return [key, value]
    }),
  )
}

export const uploadFile = async (file: File, name: string) => {
  const result = await FileUploadService.postPublicFile(file, { key: name })
  return result?.file
}

export const processImage = async (
  folder: string,
  data: any,
  propertyName: string,
  uniqueId: string,
) => {
  let imageUrl = ""
  if (data !== null && typeof data === "object") {
    const extension = data.name.split(".").pop()
    const name = imagePathName(folder, propertyName, uniqueId, extension)
    const image = await uploadFile(data, name)
    imageUrl = image || imageUrl
  } else {
    imageUrl = data
  }
  return imageUrl
}

export const removeNulls = (
  object: { [key: string]: any } | unknown,
): { [key: string]: undefined } => {
  const newObject: { [key: string]: any } = {}
  Object.entries(object).forEach(([key, value]) => {
    if (value !== null) {
      newObject[key] = value
    }
  })
  return newObject
}

export const removeKeys = (
  object: { [key: string]: any },
  keys: string | string[],
) => {
  let newObject = { ...object }
  if (isArray(keys)) {
    keys.forEach(k => {
      delete newObject[k]
    })
  } else {
    if (newObject.hasOwnProperty(keys)) {
      delete newObject[keys]
    }
  }
  return newObject
}

export const getLastDayOfMonth = (month: number, year: number) => {
  return moment({ year: year, month: month - 1 })
    .endOf("month")
    .date()
}

/** Extracts user_id from a POO object, with base64 id fallback for legacy API formats. */
export const getPooUserId = (
  poo: Partial<{ user_id: string; userId: string; id: string }>,
): string => {
  if (poo?.user_id) return poo.user_id
  if (poo?.userId) return poo.userId
  if (poo?.id) {
    try {
      const decoded = JSON.parse(atob(poo.id))
      return decoded?.user_id || decoded?.userId || ""
    } catch {
      return ""
    }
  }
  return ""
}

/**
 * Extracts a usable file/document identifier from a POO object.
 * V3 composite keys (e.g. {"user_id":"…","broker":"…","created_at":…}) don't
 * contain a separate file_id — the composite key itself is the document identifier,
 * so we return the raw value as-is.
 */
export const getPooFileId = (
  poo: Partial<{ file_id: string; fileId: string }>,
): string => {
  return poo?.file_id || poo?.fileId || ""
}
